/*
 * @Author: Christion.Young
 * @Date: 2025-09-08 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-09-08 14:00:00
 * @Description: 时间槽生成工具 - 支持复杂的预约时间计算
 */

// 默认配置
const defaultConfig = {
  interval: 60, // 时间间隔（分钟）
  weekdays: [
    { weekday: 3, workHoursStart: "05:00", workHoursEnd: "10:00" }, // 默认周三
    { weekday: 4, workHoursStart: "05:00", workHoursEnd: "10:00" }, // 默认周四
  ],
  timeHorizon: 30, // 时间范围（天数）
  HaveMadeAnAppointment: [], // 已预约的时间
};

/**
 * 生成可用的时间槽
 * @param {Object} config - 配置参数
 * @param {number} config.interval - 时间间隔（分钟）
 * @param {Array} config.weekdays - 工作日配置
 * @param {number} config.timeHorizon - 时间范围（天数）
 * @param {Array} config.HaveMadeAnAppointment - 已预约的时间
 * @returns {Array} 可用时间槽数组
 */
export function generateTimeSlots(config = {}) {
  const mergedConfig = { ...defaultConfig, ...config };
  const now = new Date();
  const days = [];
  const appointments = mergedConfig.HaveMadeAnAppointment || [];

  console.log("generateTimeSlots: 开始生成时间槽", mergedConfig);

  for (let i = 0; i < mergedConfig.timeHorizon; i++) {
    const currentDate = new Date(now);
    currentDate.setDate(now.getDate() + i);

    const dayOfWeek = currentDate.getDay(); // 0: 周日, 1: 周一, ..., 6: 周六
    const dateStr = `${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    const weekStr = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][
      dayOfWeek
    ];

    // 查找当天的工作时间配置
    const weekdayConfig = mergedConfig.weekdays?.find(
      (wc) => wc.weekday === dayOfWeek
    );
    let isWorkday = !!weekdayConfig;

    console.log(
      `generateTimeSlots: ${dateStr} (${weekStr}) 工作日配置:`,
      weekdayConfig
    );

    // 如果有全天预约，则将当天设置为非工作日
    if (
      appointments.some((app) => app.type === "date" && app.date === dateStr)
    ) {
      isWorkday = false;
      console.log(`generateTimeSlots: ${dateStr} 有全天预约，标记为非工作日`);
    }

    // 如果没有配置或被标记为非工作日，则跳过该天
    if (!isWorkday) {
      days.push({
        date: dateStr,
        week: weekStr,
        isWeekday: false,
        time: [],
      });
      continue;
    }

    const timeSlots = [];
    const appointedTimes = appointments
      .filter((app) => app.type === "time" && app.date === dateStr)
      .map((app) => app.time);

    console.log(`generateTimeSlots: ${dateStr} 已预约时间:`, appointedTimes);

    // 验证工作时间配置
    if (!weekdayConfig.workHoursStart || !weekdayConfig.workHoursEnd) {
      console.warn(
        `generateTimeSlots: ${dateStr} 工作时间配置不完整:`,
        weekdayConfig
      );
      days.push({
        date: dateStr,
        week: weekStr,
        isWeekday: false,
        time: [],
      });
      continue;
    }

    // 解析工作时间范围
    const [startHour, startMinute] = parseTime(weekdayConfig.workHoursStart);
    const [endHour, endMinute] = parseTime(weekdayConfig.workHoursEnd);

    console.log(
      `generateTimeSlots: ${dateStr} 工作时间: ${startHour}:${startMinute} - ${endHour}:${endMinute}`
    );

    // 计算当天的开始和结束时间戳
    let currentTime = new Date(
      currentDate.setHours(startHour, startMinute, 0, 0)
    );

    const endTime = new Date(currentDate.setHours(endHour, endMinute, 0, 0));

    // 生成时间槽 - 与TS版本保持一致的逻辑
    while (currentTime <= endTime) {
      const hour = currentTime.getHours();
      const minute = currentTime.getMinutes();
      const timeStr = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      // 判断是否在工作时间内、未被预约、且时间未过
      const isTimeQuantum =
        !appointedTimes.includes(timeStr) && currentTime >= now; // 新增：时间未过条件

      timeSlots.push({
        value: timeStr,
        isTimeQuantum: isTimeQuantum,
        isBooked: appointedTimes.includes(timeStr),
        isPast: currentTime < now,
      });

      // 更新当前时间
      currentTime.setMinutes(currentTime.getMinutes() + mergedConfig.interval);
    }

    console.log(
      `generateTimeSlots: ${dateStr} 生成时间槽 ${timeSlots.length} 个`
    );

    days.push({
      date: dateStr,
      week: weekStr,
      isWeekday: true,
      time: timeSlots,
    });
  }

  console.log("generateTimeSlots: 生成完成，总共", days.length, "天");
  return days;
}

/**
 * 解析时间字符串为小时和分钟
 * @param {string} timeStr - 时间字符串 (HH:mm)
 * @returns {Array} [hour, minute]
 */
function parseTime(timeStr) {
  // 添加空值检查
  if (!timeStr || typeof timeStr !== "string") {
    console.warn("parseTime: 无效的时间字符串:", timeStr);
    return [0, 0]; // 默认返回 00:00
  }

  try {
    const [hour, minute] = timeStr.split(":").map(Number);
    // 检查解析结果是否有效
    if (isNaN(hour) || isNaN(minute)) {
      console.warn("parseTime: 时间字符串格式错误:", timeStr);
      return [0, 0];
    }
    return [hour, minute];
  } catch (error) {
    console.error("parseTime: 解析时间字符串时出错:", timeStr, error);
    return [0, 0];
  }
}

/**
 * 使用时间槽的组合式函数（适配JavaScript环境）
 * @param {Object} config - 配置参数
 * @returns {Function} 返回获取时间槽的函数
 */
export function useTimeSlots(config = defaultConfig) {
  return () => generateTimeSlots(config);
}

/**
 * 计算总价格
 * @param {Array} products - 配套商品数组
 * @param {number} servePrice - 服务单价
 * @param {number} serveAmount - 服务数量
 * @returns {string} 总价格（保留两位小数）
 */
export function calculateTotalPrice(
  products = [],
  servePrice = 0,
  serveAmount = 1
) {
  let total = 0;

  // 计算配套商品总价
  products.forEach((product) => {
    if (product.isOk || product.quantity > 0) {
      const price = parseFloat(product.price || 0);
      const amount = parseInt(product.quantity || product.amount || 0);
      total += price * amount;
    }
  });

  // 计算服务总价
  const serviceTotalPrice = parseFloat(servePrice) * parseInt(serveAmount);
  total += serviceTotalPrice;

  return total.toFixed(2);
}

/**
 * 验证预约表单数据
 * @param {Object} appointmentData - 预约数据
 * @returns {Object} 验证结果
 */
export function validateAppointmentData(appointmentData) {
  const errors = [];

  // 验证预约时间
  if (appointmentData.orderType === "1") {
    // 单一接单模式
    if (!appointmentData.dateList || appointmentData.dateList.length === 0) {
      errors.push("请选择预约时间");
    }
  } else if (appointmentData.orderType === "2") {
    // 同时多单模式
    if (!appointmentData.date) {
      errors.push("请选择预约日期");
    }
    if (!appointmentData.time) {
      errors.push("请选择预约时间");
    }
  }

  // 验证联系信息
  if (!appointmentData.contactInfo?.name?.trim()) {
    errors.push("请输入联系人姓名");
  }

  if (!appointmentData.contactInfo?.phone?.trim()) {
    errors.push("请输入联系电话");
  } else if (!/^1[3-9]\d{9}$/.test(appointmentData.contactInfo.phone.trim())) {
    errors.push("请输入正确的手机号码");
  }

  // 验证服务地址（上门服务时必填）
  if (
    appointmentData.serviceWay === 2 &&
    !appointmentData.serviceAddress?.trim()
  ) {
    errors.push("请输入服务地址");
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * 格式化预约数据为API需要的格式
 * @param {Object} appointmentData - 预约数据
 * @param {Object} serviceInfo - 服务信息
 * @param {Array} goodsList - 商品列表
 * @returns {Object} 格式化后的数据
 */
export function formatAppointmentForAPI(
  appointmentData,
  serviceInfo,
  goodsList
) {
  const selectedGoods = goodsList.filter((goods) => goods.quantity > 0);

  let appointmentTimes = [];
  if (appointmentData.orderType === "1") {
    // 单一接单模式
    appointmentTimes = appointmentData.dateList.map((item) => ({
      date: item.date,
      time: item.time,
      type: "time",
    }));
  } else {
    // 同时多单模式
    appointmentTimes = [
      {
        date: appointmentData.date,
        time: appointmentData.time,
        type: "time",
      },
    ];
  }

  return {
    serviceId: serviceInfo.id,
    merchantId: serviceInfo.merchantId,
    appointmentTimes: appointmentTimes,
    contactInfo: {
      name: appointmentData.contactInfo.name.trim(),
      phone: appointmentData.contactInfo.phone.trim(),
    },
    serviceAddress: appointmentData.serviceAddress?.trim() || "",
    selectedGoods: selectedGoods.map((goods) => ({
      goodsId: goods.id,
      quantity: goods.quantity,
      price: goods.price,
    })),
    totalAmount: calculateTotalPrice(
      goodsList,
      serviceInfo.servicePrice,
      appointmentData.orderType === "1" ? appointmentData.dateList.length : 1
    ),
    orderType: appointmentData.orderType,
    serviceWay: serviceInfo.serviceWay,
    intervalTime: serviceInfo.intervalTime,
  };
}

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} wait - 等待时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  generateTimeSlots,
  useTimeSlots,
  calculateTotalPrice,
  validateAppointmentData,
  formatAppointmentForAPI,
  debounce,
};
